import { InputLabel } from "@components/Input";
import { Typography } from "@components/Typography";
import { hexToRGBA, transition } from "@styles/theme";
import { TextareaHTMLAttributes } from "react";
import styled, { css } from "styled-components";

type TextareaProps = {
    name: string;
    label: string;
    rows?: number;
    value?: string;
    placeholder?: boolean;
    disabled?: boolean;
    className?: string;
    error?: boolean;
    errorMessage?: string;
    helperText?: string;
    ref?: any;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "placeholder">;

export const Textarea = ({ name, label, rows, value, disabled, placeholder, className, error, errorMessage, helperText, ref, ...props }: TextareaProps) => {
    return (
        <Label $disabled={disabled} className={className} $error={error}>
            {placeholder ? <span className="show-for-sr">{label}</span> : <InputLabel>{label}</InputLabel>}
            <textarea
                ref={ref}
                name={name}
                rows={rows ? rows : 5}
                value={value}
                placeholder={placeholder ? label : undefined}
                disabled={disabled}
                aria-invalid={error}
                aria-describedby={`${name}-error ${name}-helper-text`}
                {...props}
            />
            {error ? (
                <Typography.Tiny id={`${name}-error`}>{errorMessage}</Typography.Tiny>
            ) : helperText ? (
                <Typography.Tiny id={`${name}-helper-text`}>{helperText}</Typography.Tiny>
            ) : null}
        </Label>
    );
};

export const Label = styled.label<{ $disabled?: boolean; $error?: boolean }>`
    display: block;
    width: 100%;

    textarea {
        display: block;
        width: 100%;
        padding: 10px 20px;
        color: ${props => props.theme.color.grayD};
        border: 1px solid ${props => props.theme.color.grayL};
        border-radius: ${props => props.theme.radius.medium};
        font-family: inherit;
        font-size: 16px;
        line-height: 22px;
        transition: color ${transition.hoverOut}, background-color ${transition.hoverOut}, border-color ${transition.hoverOut};

        &:focus {
            border-color: ${props => props.theme.color.grayD};
            transition: border-color ${transition.hoverIn};
        }

        &:disabled {
            color: ${props => hexToRGBA(props.theme.color.grayD, 0.5)};
            background-color: ${props => props.theme.color.beigeL};
            border-color: ${props => props.theme.color.grayL};
            cursor: not-allowed;
        }
    }

    ${props =>
        props.$disabled &&
        css`
            color: ${props.theme.color.inputDisabled};
        `}

    ${props =>
        props.$error &&
        css`
            color: ${props => props.theme.color.contrastRed};

            textarea {
                color: ${props => props.theme.color.contrastRed};
                background-color: ${props => props.theme.color.errorXL};
                border-color: ${props => props.theme.color.errorL};
                transition: color ${transition.hoverIn}, background-color ${transition.hoverIn}, border-color ${transition.hoverIn};

                &:focus {
                    border-color: ${props => props.theme.color.errorL};
                }
            }
        `}
`;
